import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Plus,
  RefreshCw,
  Eye,
  Edit,
  MoreHorizontal,
  HelpCircle,
  FileText,
  ClipboardList
} from 'lucide-react';

// Common Components (Assuming these paths match your project structure)
import { useToast, ToastContainer } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

// Services
import SourcingRfpService from '../../services/sourcingRFPService';

//////////////////////////////////////////////////////////////
// HELPER: MENU ITEM
//////////////////////////////////////////////////////////////

const MenuItem = ({ icon, label, onClick, danger }) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={`
      flex items-center gap-2
      w-full px-3 py-2 text-sm
      transition-colors duration-150
      ${danger
        ? "text-red-600 hover:bg-red-50"
        : "text-gray-700 hover:bg-gray-100"}
    `}
  >
    <span className={danger ? "text-red-500" : "text-gray-400"}>
      {icon}
    </span>
    {label}
  </button>
);

//////////////////////////////////////////////////////////////
// COMPONENT: ACTION DROPDOWN
//////////////////////////////////////////////////////////////

const ResponseActionDropdown = ({ response, onView, onEdit }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (!menuRef.current?.contains(e.target) && !buttonRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", close);
      window.addEventListener("scroll", close, true);
    }
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  const rect = buttonRef.current?.getBoundingClientRect();

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((prev) => !prev)}
        className={`
          flex items-center justify-center
          w-8 h-8 rounded-md border border-gray-200
          transition-all shadow-sm
          ${open ? 'bg-gray-100 border-gray-400' : 'bg-white hover:bg-gray-50'}
        `}
      >
        <MoreHorizontal size={16} className={open ? 'text-blue-600' : 'text-gray-600'} />
      </button>

      {open && rect && createPortal(
        <div
          ref={menuRef}
          style={{ position: "fixed", top: rect.bottom + 6, left: rect.left - 144, zIndex: 9999 }}
          className="w-44 bg-white border border-gray-200 rounded-lg shadow-xl py-1 animate-in fade-in zoom-in duration-100"
        >
          <MenuItem
            icon={<Eye size={16} />}
            label="View Submission"
            onClick={() => { setOpen(false); onView(response); }}
          />

          {/* Vendors can only edit if it hasn't been submitted yet */}
          {response.status === "DRAFT" && (
            <MenuItem
              icon={<Edit size={16} />}
              label="Edit Response"
              onClick={() => { setOpen(false); onEdit(response); }}
            />
          )}
          
          {response.status === "SUBMITTED" && (
            <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-gray-400 font-bold border-t mt-1">
              Submission Locked
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

const getStatusPill = (status) => {
  const config = {
    DRAFT: {
      bg: '#f3f4f6',
      color: '#6b7280',
      dot: '#6b7280',
      label: 'Draft',
    },
    SUBMITTED: {
      bg: '#d1fae5',
      color: '#059669',
      dot: '#059669',
      label: 'Submitted',
    },
  };

  // Fallback to DRAFT if status is unknown
  const s = config[status] || config.DRAFT;

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px',
      borderRadius: '999px', fontSize: '12px', fontWeight: 600, background: s.bg, color: s.color,
      border: `1px solid ${s.color}20`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot }} />
      {s.label}
    </span>
  );
};

//////////////////////////////////////////////////////////////
// MAIN COMPONENT: VENDOR RESPONSE MANAGEMENT
//////////////////////////////////////////////////////////////

const VendorResponseManagement = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // --- NEW STATE FOR SELECTOR ---
  const [availableRfps, setAvailableRfps] = useState([]);
  const [selectedRfpId, setSelectedRfpId] = useState('');

  const loadData = async () => {
  setLoading(true);
  try {
    // 1. Try loading responses (Wrapped in a sub-try so one failure doesn't kill both)
    try {
      const res = await SourcingRfpService.getResponses(''); 
      setResponses(res.data || []);
    } catch (e) {
      console.error("Response Fetch Error:", e);
      setResponses([]); // Fallback to empty list
    }

    // 2. Try loading available RFPs for the dropdown
    try {
      const rfpRes = await SourcingRfpService.getRfps(); 
      setAvailableRfps(rfpRes.data || []);
    } catch (e) {
      console.error("RFP Fetch Error:", e);
      setAvailableRfps([]);
    }

  } catch (err) {
    console.error("General Load Error:", err);
    showError("Could not connect to the sourcing service.");
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    loadData();
  }, []);
  // --- TABLE COLUMNS DEFINITION ---
  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'rfpInfo',
      label: 'RFP Details',
      render: (_, res) => (
        <div className="flex flex-col">
          <span style={{ fontWeight: 600 }} className="text-gray-900">
            {res.rfpId?.title || 'RFP Title Missing'}
          </span>
          <span className="text-xs text-gray-500">
            ID: {res.rfpId?.rfpNumber || 'N/A'}
          </span>
        </div>
      ),
    },
    {
      key: 'itemCount',
      label: 'Line Items',
      render: (_, res) => (
        <div className="flex items-center gap-2 text-gray-600">
          <ClipboardList size={14} />
          <span>{res.lineItems?.length || 0} Items</span>
        </div>
      ),
    },
    {
      key: 'totalValue',
      label: 'Total Bid Value',
      render: (_, res) => {
        const total = res.lineItems?.reduce((sum, item) => sum + (item.price || 0), 0);
        return (
          <span style={{ fontWeight: 700, color: '#111827' }}>
            {total > 0 ? `$${total.toLocaleString()}` : '—'}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, res) => getStatusPill(res.status),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (_, res) => (
        <ResponseActionDropdown
          response={res}
          onView={(r) => navigate(`/sourcing/responses/view/${r._id}`)}
          onEdit={(r) => navigate(`/sourcing/responses/edit/${r._id}`)}
        />
      ),
    },
  ];
  // --- KPI Stats (Calculated from responses) ---
  const stats = {
    totalBids: responses.length,
    pending: responses.filter(r => r.status === 'DRAFT').length,
    submitted: responses.filter(r => r.status === 'SUBMITTED').length
  };

  const headerActions = [
    {
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      variant: 'outline',
      onClick: loadData,
    },
    {
      label: 'Add Response',
      icon: <Plus size={18} />,
      variant: 'primary',
      // Disable button if no RFP is selected
      disabled: !selectedRfpId,
      onClick: () => {
        if (selectedRfpId) {
          navigate(`/sourcing/responses/add/${selectedRfpId}`);
        } else {
          showError("Please select an RFP first");
        }
      },
    },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Vendor Responses"
        description="Track and manage your proposals for open sourcing events"
        actions={headerActions}
      />

      <div className="page-container">
        {/* RFP SELECTOR BOX */}
        <div
          style={{
            marginBottom: '20px',
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            background: '#f9fafb',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}
        >
          <label style={{ fontWeight: 600, color: '#374151' }}>
            Select RFP to Quote:
          </label>
          <select
            value={selectedRfpId}
            onChange={(e) => setSelectedRfpId(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              minWidth: '320px',
              outline: 'none'
            }}
          >
            <option value="">Choose an open RFP...</option>
            {availableRfps.map((rfp) => (
              <option key={rfp._id} value={rfp._id}>
                {rfp.rfpNumber} • {rfp.title}
              </option>
            ))}
          </select>
          {!selectedRfpId && (
            <span style={{ fontSize: '12px', color: '#6b7280', italic: 'true' }}>
              Select an RFP from the list to enable "Add Response"
            </span>
          )}
        </div>

        {/* KPI CARDS (Matches your contract style) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
           <div className="border border-gray-200 bg-white p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-500">Total Proposals</div>
              <div className="text-2xl font-bold">{stats.totalBids}</div>
           </div>
           <div className="border border-gray-200 bg-white p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-500">Drafts</div>
              <div className="text-2xl font-bold text-orange-500">{stats.pending}</div>
           </div>
           <div className="border border-gray-200 bg-white p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-500">Submitted</div>
              <div className="text-2xl font-bold text-green-600">{stats.submitted}</div>
           </div>
        </div>

        <DataTable
          data={responses}
          columns={columns}
          loading={loading}
          showActions={false}
          emptyMessage="No responses created yet. Select an RFP above to start."
        />
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default VendorResponseManagement;